import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { computeTotal, type QuotationLineItem } from "@/mocks/quotationsData";
import { fetchApi } from "@/services/apiService";

type Props = {
  open: boolean;
  onClose: () => void;
  onApplyTemplate: (items: QuotationLineItem[]) => void;
};

export function TemplatePickerModal({ open, onClose, onApplyTemplate }: Props) {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchApi<any[]>("/quotations/templates", undefined, [])
      .then((res) => {
        const list = Array.isArray(res) ? res : (res as any)?.data || [];
        setTemplates(list);
      })
      .catch((err) => {
        console.error("Error fetching templates:", err);
      })
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const handleApply = (template: any) => {
    const rawItems = template.items || [];
    const items: QuotationLineItem[] = rawItems.map((item: any, idx: number) => ({
      id: idx + 1,
      description: item.description || "",
      qty: Number(item.qty) || 1,
      unit: item.unit || "Unit",
      rate: Number(item.rate) || 0,
      taxPercent: Number(item.taxPercent) || 0,
    }));
    onApplyTemplate(items);
    onClose();
    showToast(`Template "${template.name}" applied.`, "success");
  };

  const formatCurrency = (val: number) => "₹" + Number(val || 0).toLocaleString("en-IN");

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto z-10">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl z-10">
            <h2 className="text-base font-semibold text-gray-900">Choose a Template</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="p-5 space-y-3">
            {loading ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                <i className="ri-loader-4-line animate-spin text-xl mb-1 inline-block" />
                <p>Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                <i className="ri-file-list-line text-2xl text-gray-300 mb-2 block" />
                <p>No quotation templates found.</p>
              </div>
            ) : (
              templates.map((tmpl) => {
                const items = tmpl.items || [];
                const total = computeTotal(items, 0);
                return (
                  <div key={tmpl.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-gray-900">{tmpl.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          By {tmpl.createdBy || tmpl.created_by || "Admin"} · {items.length} items
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(total)}</span>
                        <button
                          onClick={() => handleApply(tmpl)}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Use This
                        </button>
                      </div>
                    </div>
                    {tmpl.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{tmpl.description}</p>
                    )}
                    <div className="mt-2 space-y-0.5">
                      {items.slice(0, 3).map((item: any, idx: number) => (
                        <p key={idx} className="text-xs text-gray-500 truncate">
                          - {item.description} — {formatCurrency(item.rate)}
                        </p>
                      ))}
                      {items.length > 3 && (
                        <p className="text-xs text-gray-400">+{items.length - 3} more items</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}